import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowUpCircle, 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  Hotel, 
  CheckCircle, 
  XCircle, 
  Clock, 
  CreditCard, 
  Building2,
  User,
  Banknote,
  Users,
  Trash2,
  Eye
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useUpgradeStore } from '../../stores/upgradeStore';
import toast from 'react-hot-toast';
import { UpgradeRequest } from '../../types/upgrade';

type Props = {
  requests: Record<string, any>;
  expandedRequest: string | null;
  setExpandedRequest: (id: string | null) => void;
  isUploading: boolean;
  userRole: string;
};

// Items per page for pagination
const ITEMS_PER_PAGE = 10;

export const RequestList = ({
  requests,
  expandedRequest,
  setExpandedRequest,
  isUploading,
  userRole
}: Props) => {
  const { translations } = useLanguage();
  const {
    approveRequest,
    rejectRequest,
    submitPayment,
    completeUpgrade,
    cancelRequest,
    acceptPayment,
    rejectPayment,
    rejectPaidUpgrade
  } = useUpgradeStore();

  const [upgradePrice, setUpgradePrice] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'date' | 'status' | 'price' | 'hotel'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const userId = localStorage.getItem('userId');

  // Group requests by pilgrim ID
  const pilgrimGroups = useMemo(() => {
    const groups: Record<string, any[]> = {};
    
    Object.values(requests).forEach(request => {
      if (!groups[request.pilgrimId]) {
        groups[request.pilgrimId] = [];
      }
      groups[request.pilgrimId].push(request);
    });
    
    // Sort requests within each group
    Object.keys(groups).forEach(pilgrimId => {
      groups[pilgrimId].sort((a, b) => b.createdAt - a.createdAt);
    });
    
    return groups;
  }, [requests]);

  // Sort groups
  const sortedGroups = useMemo(() => {
    return Object.entries(pilgrimGroups).sort(([, requestsA], [, requestsB]) => {
      const a = requestsA[0]; // Use first request in group for sorting
      const b = requestsB[0]; // Use first request in group for sorting
      
      if (sortBy === 'date') {
        return sortDirection === 'desc' ? b.createdAt - a.createdAt : a.createdAt - b.createdAt;
      } 
      else if (sortBy === 'status') {
        const statusOrder = {
          'completed': 1, 
          'paid': 2, 
          'payment_pending': 3, 
          'approved': 4, 
          'pending': 5, 
          'rejected': 6,
          'cancelled': 7
        };
        const statusA = statusOrder[a.status as keyof typeof statusOrder] || 999;
        const statusB = statusOrder[b.status as keyof typeof statusOrder] || 999;
        return sortDirection === 'desc' ? statusA - statusB : statusB - statusA;
      }
      else if (sortBy === 'price') {
        const priceA = a.price || 0;
        const priceB = b.price || 0;
        return sortDirection === 'desc' ? priceB - priceA : priceA - priceB;
      }
      else if (sortBy === 'hotel') {
        return sortDirection === 'desc' 
          ? b.fromHotel.localeCompare(a.fromHotel) 
          : a.fromHotel.localeCompare(b.fromHotel);
      }
      return 0;
    });
  }, [pilgrimGroups, sortBy, sortDirection]);

  // Handle pagination with grouped items
  const totalPages = Math.ceil(sortedGroups.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedGroups = sortedGroups.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleSortChange = (field: 'date' | 'status' | 'price' | 'hotel') => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const handleApprove = async (requestId: string) => {
    if (!upgradePrice) {
      toast.error('Please enter upgrade price');
      return;
    }

    try {
      await approveRequest(requestId, parseFloat(upgradePrice));
      toast.success('Request approved successfully');
      setUpgradePrice('');
    } catch (error: any) {
      console.error('Error approving request:', error);
      toast.error(error.message || 'Failed to approve request');
    }
  };

  const handleReject = async (requestId: string, reason: string) => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      await rejectRequest(requestId, reason);
      toast.success('Request rejected successfully');
    } catch (error: any) {
      console.error('Error rejecting request:', error);
      toast.error(error.message || 'Failed to reject request');
    }
  };

  const handleComplete = async (requestId: string) => {
    try {
      await completeUpgrade(requestId);
      toast.success('Upgrade completed successfully');
    } catch (error: any) {
      console.error('Error completing upgrade:', error);
      toast.error(error.message || 'Failed to complete upgrade');
    }
  };

  const handleCancel = async (requestId: string) => {
    try {
      await cancelRequest(requestId);
      toast.success('Request cancelled successfully');
    } catch (error: any) {
      console.error('Error cancelling request:', error);
      toast.error(error.message || 'Failed to cancel request');
    }
  };

  const handleAcceptPayment = async (requestId: string) => {
    try {
      await acceptPayment(requestId);
      toast.success('Payment accepted');
    } catch (error: any) {
      console.error('Error accepting payment:', error);
      toast.error(error.message || 'Failed to accept payment');
    }
  };

  const handleRejectPayment = async (requestId: string) => {
    try {
      await rejectPayment(requestId);
      toast.success('Payment rejected');
    } catch (error: any) {
      console.error('Error rejecting payment:', error);
      toast.error(error.message || 'Failed to reject payment');
    }
  };

  const handleRejectPaidUpgrade = async (requestId: string) => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      await rejectPaidUpgrade(requestId, rejectionReason);
      toast.success('Paid upgrade rejected successfully');
      setRejectionReason('');
    } catch (error: any) {
      console.error('Error rejecting paid upgrade:', error);
      toast.error(error.message || 'Failed to reject paid upgrade');
    }
  };

  // Format room type change display
  const getRoomTypeDisplay = (request: any) => {
    const fromType = request.fromRoomType;
    const toType = request.toRoomType || 'double'; // Default to double for old requests
    return `${fromType} → ${toType}`;
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'pending': return 'Pending';
      case 'approved': return 'Payment Pending';
      case 'payment_pending': return 'Payment Pending';
      case 'paid': return 'Paid';
      case 'completed': return 'Completed';
      case 'rejected': return 'Rejected';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const getStatusColor = (status: string): string => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'approved': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'payment_pending': return 'bg-indigo-100 text-indigo-700 border-indigo-300';
      case 'paid': return 'bg-purple-100 text-purple-700 border-purple-300';
      case 'completed': return 'bg-green-100 text-green-700 border-green-300';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-300';
      case 'cancelled': return 'bg-gray-100 text-gray-700 border-gray-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'pending': return <Clock size={16} />;
      case 'approved': return <CreditCard size={16} />;
      case 'payment_pending': return <CreditCard size={16} />;
      case 'paid': return <Banknote size={16} />;
      case 'completed': return <CheckCircle size={16} />;
      case 'rejected': return <XCircle size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  // Check if user can perform an action based on their role
  const canPerformAction = (request: UpgradeRequest, action: 'approve' | 'reject' | 'pay' | 'cancel' | 'complete' | 'rejectPaid') => {
    switch (action) {
      case 'approve':
        return userRole === 'admin' && request.status === 'pending';
      case 'reject':
        return userRole === 'admin' && request.status === 'pending';
      case 'rejectPaid':
        return userRole === 'admin' && request.status === 'paid';
      case 'pay':
        return userRole === 'organizer' && 
               (request.status === 'approved' || request.status === 'payment_pending') &&
               request.organizerId === userId;
      case 'cancel':
        return (userRole === 'guide' && request.guideId === userId && 
                ['pending', 'approved', 'payment_pending', 'paid', 'completed'].includes(request.status)) ||
               (userRole === 'organizer' && request.organizerId === userId && 
                ['pending', 'approved', 'payment_pending', 'paid', 'completed'].includes(request.status));
      case 'complete':
        return userRole === 'admin' && request.status === 'paid';
      default:
        return false;
    }
  };

  // Render action buttons based on request status and user role
  const renderActionButtons = (request: UpgradeRequest) => {
    if (userRole === 'admin' && request.status === 'pending') {
      return (
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              setUpgradePrice('');
              setExpandedRequest(request.id === expandedRequest ? null : request.id);
            }}
            className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs"
          >
            Review
          </button>
        </div>
      );
    }
    
    if (userRole === 'admin' && request.status === 'paid') {
      return (
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleComplete(request.id)}
            className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs"
          >
            Complete
          </button>
          <button
            onClick={() => {
              setRejectionReason('');
              setExpandedRequest(request.id === expandedRequest ? null : request.id);
            }}
            className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs"
          >
            Reject
          </button>
        </div>
      );
    }
    
    if (userRole === 'organizer' && 
        (request.status === 'approved' || request.status === 'payment_pending') && 
        request.organizerId === userId) {
      return (
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleAcceptPayment(request.id)}
            className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs"
          >
            Pay
          </button>
          <button
            onClick={() => handleCancel(request.id)}
            className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs"
          >
            Cancel
          </button>
        </div>
      );
    }
    
    if ((userRole === 'guide' || userRole === 'organizer') && 
        (request.status === 'pending' || request.status === 'approved' || 
         request.status === 'payment_pending' || request.status === 'paid' || 
         request.status === 'completed')) {
      if ((userRole === 'guide' && request.guideId === userId) || 
          (userRole === 'organizer' && request.organizerId === userId)) {
        return (
          <button
            onClick={() => handleCancel(request.id)}
            className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs"
          >
            Cancel
          </button>
        );
      }
    }
    
    return null;
  };

  // Render the admin review panel only when a request is expanded and it's pending approval
  const renderAdminReviewPanel = (request: UpgradeRequest) => {
    if (userRole === 'admin' && request.status === 'pending' && request.id === expandedRequest) {
      return (
        <div className="border-t pt-4 mt-2 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upgrade Price (SAR)
            </label>
            <input
              type="number"
              value={upgradePrice}
              onChange={(e) => setUpgradePrice(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Enter price..."
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={() => handleApprove(request.id)}
              disabled={!upgradePrice}
              className={`px-4 py-2 rounded-lg text-white ${
                !upgradePrice ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'
              }`}
            >
              Approve
            </button>
            <button
              onClick={() => {
                const reason = prompt('Enter reason for rejection:');
                if (reason) {
                  handleReject(request.id, reason);
                }
              }}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
            >
              Reject
            </button>
          </div>
        </div>
      );
    }
    
    // Render the admin reject paid upgrade panel
    if (userRole === 'admin' && request.status === 'paid' && request.id === expandedRequest) {
      return (
        <div className="border-t pt-4 mt-2 grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rejection Reason (After Payment)
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Enter reason for rejecting this paid upgrade..."
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setExpandedRequest(null)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={() => handleRejectPaidUpgrade(request.id)}
              disabled={!rejectionReason.trim()}
              className={`px-4 py-2 rounded-lg text-white ${
                !rejectionReason.trim() ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
              }`}
            >
              Reject Paid Upgrade
            </button>
          </div>
        </div>
      );
    }
    
    return null;
  };

  if (Object.keys(pilgrimGroups).length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg">
        <p className="text-gray-500">
          {translations?.roomUpgrade?.noRequests || 'No upgrade requests found'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Sort Controls */}
      <div className="bg-white p-3 rounded-lg shadow-sm flex flex-wrap gap-2">
        <button 
          onClick={() => handleSortChange('date')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            sortBy === 'date' 
              ? 'bg-primary-100 text-primary-700' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Calendar size={14} />
          <span>Date</span>
          {sortBy === 'date' && (
            sortDirection === 'desc' ? <ChevronRight size={14} /> : <ChevronLeft size={14} />
          )}
        </button>
        
        <button 
          onClick={() => handleSortChange('status')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            sortBy === 'status' 
              ? 'bg-primary-100 text-primary-700' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Clock size={14} />
          <span>Status</span>
          {sortBy === 'status' && (
            sortDirection === 'desc' ? <ChevronRight size={14} /> : <ChevronLeft size={14} />
          )}
        </button>
        
        <button 
          onClick={() => handleSortChange('hotel')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            sortBy === 'hotel' 
              ? 'bg-primary-100 text-primary-700' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Building2 size={14} />
          <span>Hotel</span>
          {sortBy === 'hotel' && (
            sortDirection === 'desc' ? <ChevronRight size={14} /> : <ChevronLeft size={14} />
          )}
        </button>
        
        <button 
          onClick={() => handleSortChange('price')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            sortBy === 'price' 
              ? 'bg-primary-100 text-primary-700' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Banknote size={14} />
          <span>Price</span>
          {sortBy === 'price' && (
            sortDirection === 'desc' ? <ChevronRight size={14} /> : <ChevronLeft size={14} />
          )}
        </button>
      </div>

      {/* Request Cards */}
      <div className="space-y-3">
        {paginatedGroups.map(([pilgrimId, groupRequests]) => {
          // Get first request to display pilgrim info
          const firstRequest = groupRequests[0];
          const hasMultipleRequests = groupRequests.length > 1;
          
          return (
            <motion.div
              key={pilgrimId}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm overflow-hidden"
            >
              {/* Pilgrim Header - Always visible */}
              <div className="p-2 bg-gray-50 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-primary-50">
                    <User className="text-primary-500" size={16} />
                  </div>
                  <div>
                    <h3 className="font-medium text-sm">{firstRequest.pilgrimName}</h3>
                    <div className="text-xs text-gray-500">
                      {firstRequest.passportNumber} • {firstRequest.organizerName}
                    </div>
                  </div>
                </div>
                
                {hasMultipleRequests && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs">
                    <Users size={12} />
                    <span>{groupRequests.length} requests</span>
                  </div>
                )}
              </div>

              {/* Request Cards */}
              <div className="divide-y divide-gray-100">
                {groupRequests.map((request) => (
                  <div key={request.id} className="px-3 py-2">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      {/* Status Badge */}
                      <div className="col-span-2">
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${getStatusColor(request.status)}`}>
                          {getStatusIcon(request.status)}
                          <span className="hidden sm:inline">{getStatusLabel(request.status)}</span>
                        </div>
                      </div>
                      
                      {/* Hotel & Room */}
                      <div className="col-span-3 truncate">
                        <div className="flex items-center gap-1.5">
                          <Hotel className="text-blue-500 shrink-0" size={14} />
                          <span className="text-xs font-medium truncate" title={request.fromHotel}>
                            {request.fromHotel}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 block">{getRoomTypeDisplay(request)}</span>
                      </div>
                      
                      {/* Dates & Price */}
                      <div className="col-span-3">
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <Calendar size={12} className="shrink-0" />
                          {request.startDate && request.endDate ? (
                            <span className="truncate">{request.startDate} - {request.endDate}</span>
                          ) : (
                            <span className="text-gray-400">No dates</span>
                          )}
                        </div>
                        {request.price && (
                          <div className="text-xs font-medium">{request.price} SAR</div>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="col-span-4 flex justify-end gap-1">
                        {renderActionButtons(request)}
                        
                        {/* Review button for admins only on pending requests */}
                        {userRole === 'admin' && request.status === 'pending' && (
                          <button
                            onClick={() => setExpandedRequest(request.id === expandedRequest ? null : request.id)}
                            className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs"
                          >
                            {request.id === expandedRequest ? 'Close' : 'Review'}
                          </button>
                        )}
                        
                        {/* Review button for admins on paid requests */}
                        {userRole === 'admin' && request.status === 'paid' && (
                          <button
                            onClick={() => setExpandedRequest(request.id === expandedRequest ? null : request.id)}
                            className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs"
                          >
                            {request.id === expandedRequest ? 'Close' : 'Options'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Admin Review Panel - Only shown when a pending request is expanded */}
                    {renderAdminReviewPanel(request)}
                  </div>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`p-1 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRight size={20} />
            </button>

            <span className="text-sm text-gray-700">
              {translations?.pagination?.page || 'Page'} {currentPage}{' '}
              {translations?.pagination?.of || 'of'} {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`p-1 rounded-lg ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          <div className="text-sm text-gray-500">
            {sortedGroups.length} {translations?.pagination?.items || 'pilgrim'}
          </div>
        </div>
      )}
    </div>
  );
};